import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";
import { mapActions, mapState } from "pinia";

import { IPatientCalendarRequestParams, PatientService } from "../../../services/patient.service";
import { ITaskByPatientRequest, ITaskByPatientResponse, TaskService } from "../../../services/task.service";
import { IPatientCalendarFilterOptions } from "../../../resources-vue/vue-interfaces/i-calendar";
import { debounce } from "../../../common/vue-helpers/debounce";
import { IVisitMapRequestParams } from "../../../resources-vue/vue-interfaces/i-visit-map";
import { formatFullAddress } from "../../../common/vue-helpers/modifiers/value-modifier";
import { PatientCalendarModel } from "./PatientCalendar.model";
import { PAGE_FAIL_RESPONSE } from "../../CentralIntake/Inquiry/CentralInquiry";
import { CalendarViewTypeEnum } from "../../../enums/CalendarViewTypeEnum";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import { useApp } from "../../App/useApp";

import CalendarFilters from "../components/CalendarFilters/CalendarFilters.vue";
import Pagination from "../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import PatientCalendarContent from "./components/PatientCalendarContent/PatientCalendarContent.vue";
import EmployeeMap from "../EmployeeCalendar/components/EmployeeMap/EmployeeMap.vue";

export default defineComponent({
    components: {
        CalendarFilters,
        Pagination,
        PatientCalendarContent,
        EmployeeMap
    },

    data(vm) {
        return {
            _taskService: null as TaskService,
            listView: CalendarViewTypeEnum.Weekly as string,
            startDate: null as Date,
            endDate: null as Date,
            mapDate: null as Date,
            mapVisitsList: [] as any,
            isError: false as boolean,
            patientCalendarPagination: null,
            selectedWeekRange: 1 as number,
            mapRefreshDebounce: null,
            visitMapDate: null,
            patientsList: {} as any,
            locationsList: {} as any,
            fetchUpdatedCount: 0 as number,
            isPatientLoading: true as boolean,
            patients: [] as PatientCalendarModel[],
            tasks: [] as ITaskByPatientResponse[],
            tasksMap: null,
            pageSize: 15 as number,
            mapFilterOptions: {
                visitStatus: [],
                date: new Date(Date.now()).toISOString()
            } as IVisitMapRequestParams,
            patientFilterOptions: {
                pageLength: 15,
                page: 1,
                term: "",
                patientStatus: null,
                agencies: [],
                locationIds: [],
                scheduleFilter: 0,
                startDate: "",
                endDate: ""
            } as IPatientCalendarRequestParams,
            taskFilterOptions: {
                start: null,
                end: null,
                patientIds: []
            } as ITaskByPatientRequest,
            tooltip: `<div class='evv-statuses'>
           <p> <i class='evv-adjusted signed'></i> Adjusted Completed EVV</p>
           <p><i class='evv-adjusted in-progress'></i> Adjusted InProgress EVV</p>
           <p><a class='evv'><i class='fa fa-home fa-md'></i></a> In Progress EVV</p>
           <p><a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a> Not Started EVV</p>
           <p><a class='evv'><i class='fa fa-home fa-md signed'></i></a> Completed EVV</p>
            </div>`,
            CalendarViewTypeEnum,
            PatientTask: [] as ITaskByPatientResponse[]
        };
    },

    created() {
        this._taskService = Container.instance.get(TaskService);
        this.patientCalendarPagination = this.$refs?.patientCalendarPagination as typeof Pagination;
        this.mapRefreshDebounce = debounce(this.mapRefresh, 1200);
    },

    mounted() {
        this.patientCalendarPagination = this.$refs?.patientCalendarPagination as typeof Pagination;
    },

    computed: {
        getHasData() {
            return this.patients?.length > 0;
        },
        ...mapState(useApp, ["showMainHeader"])
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        handleViewChange(type: string) {
            this.listView = type;
            type === CalendarViewTypeEnum.Weekly
                ? this.updateDateRange({ startDate: this.startDate, endDate: this.endDate })
                : this.mapDateUpdate({ startDate: this.mapDate, endDate: this.mapDate });
        },

        async updateDateRange(options: any, isWeekRangeChanged: boolean = true) {
            if (isWeekRangeChanged) {
                this.startDate = options.startDate;
                this.endDate = options.endDate;
            }
            this.patientFilterOptions.startDate = this.taskFilterOptions.start = this.formatDate(this.startDate);
            this.patientFilterOptions.endDate = this.taskFilterOptions.end = this.formatDate(this.endDate);
            await this.pageReset();
        },

        async mapDateUpdate(options: any, isWeekRangeChanged: boolean = true) {
            this.mapDate = options.startDate;
            debounce(this.mapRefresh, 1200);
        },

        formatDate(input: Date) {
            return `${input.getMonth() + 1}/${input.getDate()}/${input.getFullYear()}`;
        },

        async pageReset() {
            this.isError = false;
            await this.patientCalendarPagination?.reset();
        },

        async patientRefresh(
            options: IPatientCalendarFilterOptions,
            isWeekRangeChanged: boolean,
            selectedWeekRange?: number
        ) {
            this.patientFilterOptions = {
                ...this.patientFilterOptions,
                ...options
            };
            this.selectedWeekRange = selectedWeekRange;
            this.updateDateRange(options, isWeekRangeChanged);
        },

        async mapRefresh(options?: IVisitMapRequestParams) {
            if (options) {
                this.mapFilterOptions = { ...options, visitStatus: [] };
                this.visitMapDate = options.date;

                this.showLoading(true);
                if (this.mapFilterOptions.agencies.length != 0) {
                    this.mapVisitsList = await this._taskService.getCalendarMapVisits(this.mapFilterOptions);
                } else {
                    this.mapVisitsList = [];
                }
                this.showLoading(false);
            } else {
                this.mapVisitsList = await this._taskService.getCalendarMapVisits(this.mapFilterOptions);
            }

            this.patientsList = {};
            this.locationsList = {};
            for (let visit of this.mapVisitsList) {
                const name: any = visit.PatientName.FirstName + " " + visit.PatientName.LastName;
                const address: any = formatFullAddress(visit.PatientAddress);
                if (!this.patientsList[name]) {
                    this.patientsList[name] = visit.PatientId + "_" + address;
                }
                if (!this.locationsList[address]) {
                    this.locationsList[address] = visit.Id;
                }
            }
            this.fetchUpdatedCount++;
        },

        async pageChanged(pageNumber: number, pageSize: number = 15) {
            try {
                if (
                    this.patientFilterOptions?.agencies?.length > 0 &&
                    this.patientFilterOptions?.locationIds?.length > 0
                ) {
                    this.isError = false;
                    this.patients = [];
                    this.patientFilterOptions.page = pageNumber;
                    this.isPatientLoading = true;
                    let data = await this._taskService.fetchTaskRangeBypatient({
                        ...this.patientFilterOptions,
                        agencies: this.patientFilterOptions.agencies.map((x) =>
                            x.indexOf("_") !== x.lastIndexOf("_") ? x.slice(0, x.lastIndexOf("_")) : x
                        )
                    });
                    if (data) {
                        let dataItems = data.Items ?? [];
                        this.patients = data.Patient;
                        this.taskFilterOptions.patientIds = dataItems.map((x) => x.PatientId);
                        this.PatientTask = data.Items;
                        this.updateRefreshTask();
                        this.isPatientLoading = false;
                        return {
                            totalPageCount: data.PageCount,
                            success: data.ItemCount > 0,
                            totalCount: data.ItemCount
                        };
                    } else {
                        return PAGE_FAIL_RESPONSE;
                    }
                } else {
                    this.patients = [];
                    this.taskFilterOptions.patientIds = [];
                    return PAGE_FAIL_RESPONSE;
                }
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            }
        },

        async taskRefresh() {
            try {
                if (this.taskFilterOptions?.patientIds?.length > 0) {
                    const tasks = await this._taskService.getTasksByPatients(this.taskFilterOptions);
                    this.taskSort(tasks);
                }
            } catch (e) {
                console.error(e);
            }
        },
        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },

        updateRefreshTask() {
            if (this.taskFilterOptions?.patientIds?.length > 0) {
                const tasks = this.PatientTask;
                this.taskSort(tasks);
            }
        },

        taskSort(tasks: ITaskByPatientResponse[]) {
            try {
                tasks.sort((a: any, b: any) => {
                    return moment(a.StartTime).isAfter(moment(b.StartTime)) ? 1 : -1;
                });
                this.tasks = tasks;
            } catch (e) {
                console.error(e);
            }
        }
    }
});
